import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Card } from 'primereact/card';
import { Checkbox } from 'primereact/checkbox';
import { Button } from 'primereact/button';
import { Divider } from 'primereact/divider';
import { toast } from 'react-toastify';
import { updatePageLayoutSettings } from '../../store/AsyncMethods/SetupMethod';
import backendVerifiedUser from '../../api/backendVerifiedUser';

const PageLayoutManagement = () => {
  const dispatch = useDispatch();

  const [showProducts, setShowProducts] = useState(0);
  const [showCollections, setShowCollections] = useState(0);
  const [useFrontpageFilter, setUseFrontpageFilter] = useState(1);
  const [showShopByCategory, setShowShopByCategory] = useState(1);
  const [loading, setLoading] = useState(false);

  // Direct API call — Redux bypass karo taake overwrite na ho
  useEffect(() => {
    backendVerifiedUser.get('/setup-details').then((res) => {
      const d = res.data;
      if (!d) return;
      setShowProducts(d.show_products != null ? Number(d.show_products) : 0);
      setShowCollections(d.show_collections != null ? Number(d.show_collections) : 0);
      setUseFrontpageFilter(d.use_frontpage_filter != null ? Number(d.use_frontpage_filter) : 1);
      setShowShopByCategory(d.show_shop_by_category != null ? Number(d.show_shop_by_category) : 1);
    }).catch(() => {});
  }, []);

  const handleSave = async () => {
    setLoading(true);
    const data = {
      show_products: showProducts ? 1 : 0,
      show_collections: showCollections ? 1 : 0,
      use_frontpage_filter: useFrontpageFilter ? 1 : 0,
      show_shop_by_category: showShopByCategory ? 1 : 0,
    };
    try {
      const result = await dispatch(updatePageLayoutSettings(data));
      if (result) {
        toast.success('Settings saved successfully');
        // Backend se confirmed values directly state mein set karo
        if (result && typeof result === 'object') {
          setShowProducts(result.show_products != null ? Number(result.show_products) : data.show_products);
          setShowCollections(result.show_collections != null ? Number(result.show_collections) : data.show_collections);
          setUseFrontpageFilter(result.use_frontpage_filter != null ? Number(result.use_frontpage_filter) : data.use_frontpage_filter);
          setShowShopByCategory(result.show_shop_by_category != null ? Number(result.show_shop_by_category) : data.show_shop_by_category);
        }
      } else {
        toast.error('Failed to save settings');
      }
    } catch {
      toast.error('An error occurred while saving');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    backendVerifiedUser.get('/setup-details').then((res) => {
      const d = res.data;
      if (!d) return;
      setShowProducts(d.show_products != null ? Number(d.show_products) : 0);
      setShowCollections(d.show_collections != null ? Number(d.show_collections) : 0);
      setUseFrontpageFilter(d.use_frontpage_filter != null ? Number(d.use_frontpage_filter) : 1);
      setShowShopByCategory(d.show_shop_by_category != null ? Number(d.show_shop_by_category) : 1);
      toast.info('Settings reset to saved values');
    }).catch(() => {});
  };

  return (
    <div className="p-4">
      <div className="mb-4">
        <h2 className="text-2xl font-bold text-gray-800 mb-2">Page Layout Management</h2>
        <p className="text-gray-600">Configure which features and sections to display on your website</p>
      </div>

      <Card className="mb-4">
        <h3 className="text-lg font-semibold text-gray-700 mb-4">Feature Visibility Settings</h3>
        <Divider />

        <div className="flex flex-col gap-4 mt-4">
          <div className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
            <Checkbox
              inputId="show_products"
              checked={showProducts === 1}
              onChange={(e) => setShowProducts(e.checked ? 1 : 0)}
              className="p-checkbox"
            />
            <label htmlFor="show_products" className="cursor-pointer flex-1">
              <div className="font-semibold text-gray-800">Show Our Feature Products</div>
              <div className="text-sm text-gray-600">Enable this to display "Our Feature Products" section on the homepage</div>
            </label>
          </div>

          <div className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
            <Checkbox
              inputId="show_collections"
              checked={showCollections === 1}
              onChange={(e) => setShowCollections(e.checked ? 1 : 0)}
              className="p-checkbox"
            />
            <label htmlFor="show_collections" className="cursor-pointer flex-1">
              <div className="font-semibold text-gray-800">Show Collections</div>
              <div className="text-sm text-gray-600">Enable this to display "Discover Our Collections" section on the homepage</div>
            </label>
          </div>

          <div className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
            <Checkbox
              inputId="show_shop_by_category"
              checked={showShopByCategory === 1}
              onChange={(e) => setShowShopByCategory(e.checked ? 1 : 0)}
              className="p-checkbox"
            />
            <label htmlFor="show_shop_by_category" className="cursor-pointer flex-1">
              <div className="font-semibold text-gray-800">Show Shop By Category</div>
              <div className="text-sm text-gray-600">Enable this to display "Shop By Category" section on the homepage</div>
            </label>
          </div>

          <Divider align="left">
            <span className="text-sm text-gray-500 font-medium">Product Display Mode</span>
          </Divider>

          <div className={`flex items-start gap-3 p-4 rounded-lg border-2 transition-colors ${useFrontpageFilter === 1 ? 'border-blue-400 bg-blue-50' : 'border-orange-400 bg-orange-50'}`}>
            <Checkbox
              inputId="use_frontpage_filter"
              checked={useFrontpageFilter === 1}
              onChange={(e) => setUseFrontpageFilter(e.checked ? 1 : 0)}
              className="p-checkbox mt-1"
            />
            <label htmlFor="use_frontpage_filter" className="cursor-pointer flex-1">
              <div className="font-semibold text-gray-800 flex items-center gap-2">
                Show only "Frontpage" marked products
                <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${useFrontpageFilter === 1 ? 'bg-blue-500 text-white' : 'bg-orange-500 text-white'}`}>
                  {useFrontpageFilter === 1 ? 'FILTER ON' : 'ALGORITHM MODE'}
                </span>
              </div>
              <div className="text-sm text-gray-600 mt-1">
                {useFrontpageFilter === 1
                  ? '✅ Sirf woh products dikhaye jayenge jinpar admin ne "Show on Frontpage" ON kiya hua hai (product table mein)'
                  : '🤖 Sab active products available honge — "You May Also Like" algorithm user ki history dekh ke suggest karega'}
              </div>
            </label>
          </div>
        </div>
      </Card>

      <div className="flex gap-3 justify-end">
        <Button
          label="Reset"
          icon="pi pi-refresh"
          className="p-button-secondary"
          onClick={handleReset}
          disabled={loading}
        />
        <Button
          label="Save Changes"
          icon="pi pi-check"
          className="p-button-primary"
          onClick={handleSave}
          loading={loading}
        />
      </div>
    </div>
  );
};

export default PageLayoutManagement;
